import { DateTime } from 'luxon';
import { ParsedUrlQuery } from 'querystring';

/**
 * @name buildUrl
 * @description Returns a URL for the given route name.
 */
export function buildUrl(path: string) {
  const isBrowser = typeof window !== 'undefined';
  const url = isBrowser ? `${window.location.origin}${path}` : '';

  return url;
}

export type QueryParams = {
  clients?: string[];
  endDate?: Date;
  handles?: string[];
  page?: number;
  startDate?: Date;
  statuses?: string[];
  tags?: string[];
};
/**
 * @name encodeQueryParams
 * @description Encodes the given query params into a query for Next.js.
 */
export function encodeQueryParams(params: QueryParams): ParsedUrlQuery {
  const { clients, endDate, handles, page, startDate, statuses, tags } = params;
  const ret: ParsedUrlQuery = {};

  if (clients && clients?.length) {
    ret.clients = clients instanceof Array ? clients.join(',') : [clients];
  }

  if (tags && tags?.length) {
    ret.tags = tags instanceof Array ? tags.join(',') : [tags];
  }

  if (handles && handles?.length > 0) {
    ret.handles = handles instanceof Array ? handles.join(',') : [handles];
  }

  if (statuses && statuses?.length > 0) {
    ret.statuses = statuses instanceof Array ? statuses.join(',') : [statuses];
  }

  if (startDate) {
    ret.startDate = DateTime.fromJSDate(startDate).toMillis().toString();
  }

  if (endDate) {
    ret.endDate = DateTime.fromJSDate(endDate).toMillis().toString();
  }

  if (page) {
    ret.page = page.toString();
  }

  return ret;
}

/**
 * @name decodeQueryParams
 * @description Gets all the query params from the URL in a more usable format.
 */
export function decodeQueryParams(query: ParsedUrlQuery): QueryParams {
  const ret: QueryParams = {};

  if (query.clients) {
    const clients = query.clients as string;
    ret.clients = clients.split(',');
  }

  if (query.tags) {
    const tags = query.tags as string;
    ret.tags = tags.split(',');
  }

  if (query.handles) {
    const handles = query.handles as string;
    ret.handles = handles.split(',');
  }

  if (query.statuses) {
    const statuses = query.statuses as string;
    ret.statuses = statuses.split(',');
  }

  if (query.startDate) {
    const startDateMillis = parseInt(query.startDate as string, 10);
    const startDate = DateTime.fromMillis(startDateMillis).toJSDate();
    ret.startDate = startDate;
  }

  if (query.endDate) {
    const endDateMillis = parseInt(query.endDate as string, 10);
    const endDate = DateTime.fromMillis(endDateMillis).toJSDate();
    ret.endDate = endDate;
  }

  if (query.page) {
    const page = parseInt(query.page as string, 10);
    ret.page = page;
  }

  return ret;
}
